/**
 * AdminPageKit — the page-level chrome shared by the admin list pages
 * (SIP Trunks, Outbound Caller ID, Transfer Rules, IVR …).
 *
 * Pairs with `DrawerFormKit` (the create/edit drawer pieces). Between them a
 * list page is: `PageHeader` + `PrimaryAction` → `SummaryTile` row →
 * `TableCard` wrapping the shared `ResourceTable`.
 *
 * All presentation, no data fetching — the page owns its hook and passes rows
 * down.
 */
import type { JSX, ReactNode } from 'react'

/** House accent — matches the drawer header chip and Save button. */
export const ACCENT = '#3454cf'

/**
 * The page's primary action (usually "New <thing>").
 *
 * A plain button rather than PrimeReact's, so the page action and the drawer's
 * Save button are visibly the same control.
 */
export function PrimaryAction({
  label,
  icon = 'pi pi-plus',
  onClick,
  disabled = false,
}: {
  label: string
  icon?: string
  onClick: () => void
  disabled?: boolean
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-sm bg-[#3454cf] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#2a45b0] hover:shadow-md disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
    >
      <i className={`${icon} text-xs`} aria-hidden="true" />
      {label}
    </button>
  )
}

const TILE_ACCENT: Record<'slate' | 'emerald' | 'indigo' | 'amber', string> = {
  slate: '#64748b',
  emerald: '#10b981',
  indigo: '#6366f1',
  amber: '#f59e0b',
}

export type TileTone = keyof typeof TILE_ACCENT

/** A single count tile for the summary row above a table. */
export function SummaryTile({
  label,
  value,
  icon: _icon,
  tone = 'slate',
}: {
  label: string
  value: number | string
  icon: string
  tone?: TileTone
}): JSX.Element {
  return (
    <div
      className="bg-white rounded-md border border-gray-100 border-l-4 p-5 shadow-sm"
      style={{ borderLeftColor: TILE_ACCENT[tone] }}
    >
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1.5 tracking-tight">{value}</p>
    </div>
  )
}

/**
 * Grid wrapper for a row of `SummaryTile`s. Defaults to three across; pass
 * `columns={4}` for resources that report a fourth figure (queues and DIDs
 * both split their totals four ways).
 */
export function SummaryTiles({
  children,
  columns = 3,
}: {
  children: ReactNode
  columns?: 3 | 4
}): JSX.Element {
  const gridCols = columns === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4 mb-4`}>{children}</div>
  )
}

/**
 * Bordered card that wraps a table with a toolbar header: title, a
 * "<shown> of <total> shown" subtitle, and an optional search box.
 *
 * Search is a controlled plain input rather than PrimeReact's, so it matches
 * the rest of this kit; pass `search` to render it.
 */
export function TableCard({
  title,
  shown,
  total,
  search,
  actions,
  children,
}: {
  title: string
  shown: number
  total: number
  search?: {
    value: string
    onChange: (value: string) => void
    placeholder: string
    /** Accessible name for the input, e.g. "Search SIP trunks". */
    ariaLabel: string
  }
  /** Extra controls rendered left of the search box. */
  actions?: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50/70 px-5 py-3.5">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-500">
            {shown} of {total} shown
          </p>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          {search && (
            <div className="relative">
              <i
                className="pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                aria-label={search.ariaLabel}
                placeholder={search.placeholder}
                className="w-64 rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#3454cf] focus:outline-none focus:ring-2 focus:ring-[#3454cf]/15"
              />
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Table cells
// ---------------------------------------------------------------------------

/**
 * Active / Inactive pill for a boolean status column. Shared so the same
 * status reads identically on every admin list.
 */
export function StatusPill({ active }: { active: boolean }): JSX.Element {
  return active ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 ring-1 ring-inset ring-gray-200">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" aria-hidden="true" />
      Inactive
    </span>
  )
}

/**
 * The leading identity cell: icon chip, name, and a muted subtitle (usually
 * the description). `subtitle` falls back to a placeholder so rows keep a
 * consistent height whether or not a description was filled in.
 */
export function NameCell({
  icon,
  name,
  subtitle,
  fallback = 'No description',
}: {
  icon: string
  name: string
  subtitle?: string | null | undefined
  fallback?: string
}): JSX.Element {
  const rawSubtitle = subtitle !== null && subtitle !== undefined && subtitle !== '' ? subtitle : fallback
  const truncatedSubtitle = rawSubtitle.length > 10 ? `${rawSubtitle.slice(0, 10)}…` : rawSubtitle
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#3454cf]/10 text-[#3454cf]">
        <i className={`${icon} text-xs`} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="truncate font-semibold text-gray-900">{name}</div>
        <div className="truncate text-xs text-gray-500" title={rawSubtitle}>
          {truncatedSubtitle}
        </div>
      </div>
    </div>
  )
}

/** Monospaced cell for phone numbers, hosts and other machine values. */
export function MonoCell({ value }: { value: string }): JSX.Element {
  return <span className="font-mono text-[13px] text-gray-800">{value}</span>
}

/** Small uppercase chip for short enum-ish values (type, format, codec). */
export function TagCell({ value }: { value: string }): JSX.Element {
  return (
    <span className="inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-inset ring-slate-200">
      {value}
    </span>
  )
}

/**
 * An inline alert strip (warning by default) for page-level notices — e.g.
 * "telephony stack reload required", "no caller IDs configured".
 */
export function NoticeBar({
  tone = 'amber',
  icon = 'pi pi-exclamation-triangle',
  children,
  role = 'alert',
}: {
  tone?: 'amber' | 'red' | 'blue'
  icon?: string
  children: ReactNode
  role?: 'alert' | 'status'
}): JSX.Element {
  const tones = {
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-200 bg-red-50 text-red-800',
    blue: 'border-blue-200 bg-blue-50 text-blue-800',
  } as const
  const iconTones = {
    amber: 'text-amber-500',
    red: 'text-red-500',
    blue: 'text-blue-500',
  } as const

  return (
    <div
      role={role}
      className={`mb-4 flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${tones[tone]}`}
    >
      <i className={`${icon} mt-0.5 ${iconTones[tone]}`} aria-hidden="true" />
      <span>{children}</span>
    </div>
  )
}
